import type { TraceEnvironment } from "./conversationsSearch";

/**
 * How a pasted value becomes one session (fe/CONVERSATIONS.md §3.1).
 *
 * A value that names a session opens it, skipping the list; anything else is
 * a search of the list. The server only resolves a full UUID —
 * `/admin/sessions/{id}/messages` takes nothing else, though in any spelling
 * Python's `uuid.UUID` reads — and there is no prefix search to lean on (the
 * spec rules out growing one: §5). So the short form `12ef…88b0` the screen
 * prints on its chips is resolved **here, against this viewer's recent
 * lookups**, and only there. A short form that matches nothing is not guessed
 * at — it goes to the list as a search, where the empty result says a new
 * conversation needs all 32 hex digits.
 */

export type RecentLookup = { id: string; env: TraceEnvironment };

export type LookupResult =
  | { kind: "full"; id: string }
  | { kind: "recent"; entry: RecentLookup }
  /** `hexDigits` so the screen can say "16 of 32" — a truncated paste shows. */
  | { kind: "short_unknown"; hexDigits: number }
  | { kind: "empty" }
  | { kind: "invalid" };

const UUID_SOURCE = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const UUID_EXACT = new RegExp(`^${UUID_SOURCE}$`, "i");
const UUID_ANYWHERE = new RegExp(UUID_SOURCE, "i");
// 32 hex digits with no hyphens, standing alone inside longer text.
const BARE_HEX_ANYWHERE = /(?:^|[^0-9a-f])([0-9a-f]{32})(?![0-9a-f])/;
// `…` is what the chips print; `...` is what a keyboard types.
const SHORT_FORM = /^([0-9a-f-]{2,})\s*(?:…|\.{2,3})\s*([0-9a-f-]{2,})$/i;
const FRAGMENT = /^[0-9a-f-]{4,}$/i;

export const RECENT_LIMIT = 6;

/** True only for the canonical 8-4-4-4-12 form — see `canonicalSessionId`. */
export function isSessionId(value: string | null | undefined): value is string {
  return typeof value === "string" && UUID_EXACT.test(value);
}

/**
 * The canonical 8-4-4-4-12 form of anything Python's `uuid.UUID()` accepts,
 * or null.
 *
 * That is the server's parser for the path parameter, and the old search page
 * handed the input straight to it — so it is the set of spellings that "used
 * to work": `urn:uuid:` prefix, braces, and hyphens anywhere or nowhere, as
 * long as 32 hex digits remain. Accepting only the hyphenated form turned an
 * id pasted as 32 bare hex digits into a "short form" nobody had opened.
 */
export function canonicalSessionId(raw: string): string | null {
  const hex = raw
    .trim()
    .toLowerCase()
    .replace(/^urn:/, "")
    .replace(/^uuid:/, "")
    .replace(/^\{+|\}+$/g, "")
    .replace(/-/g, "");
  if (!/^[0-9a-f]{32}$/.test(hex)) return null;
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

/** The form the chips print: first four, last four. */
export function shortSessionId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
}

/**
 * Read what was pasted.
 *
 * A full id wins in any spelling the server accepts (`canonicalSessionId`),
 * and wherever it sits in the input, so pasting a whole admin URL or a log
 * line works. Anything shorter is only ever matched against recents, newest
 * first.
 */
export function resolveSessionQuery(
  raw: string,
  recents: readonly RecentLookup[],
): LookupResult {
  const value = raw.trim().toLowerCase();
  if (!value) return { kind: "empty" };

  const whole = canonicalSessionId(value);
  if (whole) return { kind: "full", id: whole };

  const embedded = value.match(UUID_ANYWHERE)?.[0] ?? value.match(BARE_HEX_ANYWHERE)?.[1];
  if (embedded) return { kind: "full", id: canonicalSessionId(embedded) ?? embedded };

  // Hyphens are compared away on both sides, as the server's parser does —
  // `3f2c9a1e4b5d` is the same fragment as `3f2c9a1e-4b5d`.
  const short = value.match(SHORT_FORM);
  if (short) {
    const head = stripHyphens(short[1]);
    const tail = stripHyphens(short[2]);
    const entry = recents.find((recent) => {
      const id = stripHyphens(recent.id);
      return id.startsWith(head) && id.endsWith(tail);
    });
    return entry
      ? { kind: "recent", entry }
      : { kind: "short_unknown", hexDigits: head.length + tail.length };
  }

  if (FRAGMENT.test(value)) {
    const fragment = stripHyphens(value);
    const entry = recents.find((recent) => stripHyphens(recent.id).includes(fragment));
    return entry
      ? { kind: "recent", entry }
      : { kind: "short_unknown", hexDigits: fragment.length };
  }

  return { kind: "invalid" };
}

function stripHyphens(value: string) {
  return value.replace(/-/g, "");
}

/** Newest first, no duplicates, capped. */
export function rememberLookup(
  recents: readonly RecentLookup[],
  entry: RecentLookup,
): RecentLookup[] {
  const rest = recents.filter(
    (recent) => !(recent.id === entry.id && recent.env === entry.env),
  );
  return [entry, ...rest].slice(0, RECENT_LIMIT);
}

// --- Storage ---------------------------------------------------------------
// A per-viewer convenience, so browser storage and nothing more. It can be
// empty or throw (private windows, blocked site data); the screen works the
// same without it, it just has no chips.

const STORAGE_KEY = "sams.conversations.recent";

export function loadRecentLookups(): RecentLookup[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is RecentLookup =>
          typeof item === "object" &&
          item !== null &&
          isSessionId((item as RecentLookup).id) &&
          ((item as RecentLookup).env === "dev" || (item as RecentLookup).env === "prod"),
      )
      .slice(0, RECENT_LIMIT);
  } catch {
    return [];
  }
}

export function saveRecentLookups(recents: readonly RecentLookup[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recents));
  } catch {
    // Storage refused — the chips just will not survive a reload.
  }
}
